/*
 * Floating WhatsApp Action Button (FAB)
 * Persists while scrolling – botanical green theme
 * Opens WhatsApp with pre-filled Hebrew message
 */

import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, X } from "lucide-react";

export default function FloatingWhatsApp() {
  const [visible, setVisible] = useState(false);
  const [tooltip, setTooltip] = useState(true);

  // Show FAB after user scrolls past hero
  useEffect(() => {
    const handleScroll = () => {
      setVisible(window.scrollY > 300);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  // Auto-hide tooltip after 4 seconds
  useEffect(() => {
    if (visible && tooltip) {
      const timer = setTimeout(() => setTooltip(false), 4000);
      return () => clearTimeout(timer);
    }
  }, [visible, tooltip]);

  const handleClick = () => {
    const message = "היי שרון, אני רוצה להתאמן!";
    const url = `https://wa.me/9720546362929?text=${encodeURIComponent(message)}`;
    window.open(url, "_blank");
  };

  return (
    <AnimatePresence>
      {visible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.5, y: 20 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.5, y: 20 }}
          transition={{ type: "spring", stiffness: 300, damping: 25 }}
          className="fixed bottom-6 left-6 z-50 flex items-center gap-3"
          style={{ direction: "rtl" }}
        >
          {/* Tooltip bubble */}
          <AnimatePresence>
            {tooltip && (
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -10 }}
                transition={{ duration: 0.3 }}
                className="relative flex items-center gap-2 bg-white text-[#1B4332] text-sm font-semibold px-4 py-2.5 rounded-full shadow-lg border border-[#2D5A3D]/10"
                style={{ fontFamily: "var(--font-body)" }}
              >
                היי שרון, אני רוצה להתאמן!
                {/* Arrow pointing right toward button */}
                <span className="absolute -left-2 top-1/2 -translate-y-1/2 w-0 h-0 border-t-[6px] border-t-transparent border-b-[6px] border-b-transparent border-r-[8px] border-r-white" />
                <button
                  onClick={() => setTooltip(false)}
                  className="text-[#2D5A3D]/40 hover:text-[#2D5A3D] transition-colors"
                  aria-label="סגור"
                >
                  <X className="w-3.5 h-3.5" />
                </button>
              </motion.div>
            )}
          </AnimatePresence>

          {/* Main FAB button */}
          <motion.button
            onClick={handleClick}
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.95 }}
            className="relative w-16 h-16 rounded-full shadow-2xl flex items-center justify-center focus:outline-none focus-visible:ring-4 focus-visible:ring-[#25D366]/50"
            style={{
              background: "linear-gradient(135deg, #25D366 0%, #1a9d4f 100%)",
            }}
            aria-label="שלחי הודעה בוואטסאפ"
          >
            {/* Pulse ring */}
            <span className="absolute inset-0 rounded-full bg-[#25D366]/40 animate-ping" />
            <MessageCircle className="w-8 h-8 text-white relative z-10" />
          </motion.button>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
